import { isoDate } from '@cf/shared';
import type { IsoDate } from '@cf/shared';
import { taxYearContaining } from '@cf/engine';
import { LATEST_RULE_SET_START_YEAR } from '@cf/tax';

/**
 * The application's ONE wall-clock read.
 *
 * The engine bans ambient date reads outright (hard rule #6) so projections
 * stay reproducible, which means the as-of date has to enter from outside —
 * and it has to enter from exactly one place. It previously did not: each data
 * client defaulted to a hard-coded `2025-04-06` / `2025`, so a projection
 * produced on 27 July 2026 opened in the 2025/26 tax year, a year that had
 * already ended. Every "today's value" figure in that run was therefore
 * deflated to a year already in the past.
 *
 * Read once here, derived once here, injected everywhere. Tests pass an
 * explicit date instead of stubbing a global.
 */

/**
 * Today as an ISO date, from LOCAL calendar components.
 *
 * `toISOString()` would be wrong: west of Greenwich it can report yesterday,
 * and around the 6 April boundary that silently moves the projection into the
 * previous tax year — the exact class of defect this module exists to stop.
 */
export function todayIsoDate(now: Date = new Date()): IsoDate {
  const year = now.getFullYear().toString().padStart(4, '0');
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  return isoDate(`${year}-${month}-${day}`);
}

/**
 * The projection clock for this session: the as-of date and the tax year
 * CONTAINING it, so the first projected year is always the live one.
 */
export interface AppClock {
  readonly asOfDate: IsoDate;
  readonly startYear: number;
  /**
   * The tax year the as-of date actually falls in, before clamping. Equal to
   * `startYear` in the normal case; higher once the calendar has moved past the
   * newest rule set this build ships.
   */
  readonly actualTaxYear: number;
  /**
   * True when `startYear` had to be held back because no rule set exists yet
   * for the live tax year. The UI must say so — every figure is then computed
   * on last year's rules.
   */
  readonly clampedToLatestRules: boolean;
}

export function makeAppClock(asOfDate: IsoDate = todayIsoDate()): AppClock {
  const actualTaxYear = taxYearContaining(asOfDate);
  // Deriving the start year from the real clock (rather than the hard-coded
  // 2025 this replaced) exposes a cliff the constant used to hide: on 6 April
  // of the year after the newest shipped rule set, `rulesForTaxYear` throws and
  // EVERY projection in the app fails. Its fail-closed stance is right — a
  // missing rule set must never be silently treated as nil — so the boundary
  // clamps instead, and flags that it did. An adviser must be told the figures
  // are on last year's rules; they must not lose the tool overnight, and they
  // must not be shown a raw engine error.
  const startYear = Math.min(actualTaxYear, LATEST_RULE_SET_START_YEAR);
  return Object.freeze({
    asOfDate,
    startYear,
    actualTaxYear,
    clampedToLatestRules: startYear !== actualTaxYear,
  });
}

/**
 * The composition-root clock. Evaluated once at module load, so every data
 * client and every preview in a session shares one as-of date — a session that
 * crosses midnight (or 6 April) cannot produce two runs anchored differently.
 */
export const APP_CLOCK: AppClock = makeAppClock();
